import { emailSubscriptionsApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  EmailSubscription,
  MeUpdateStocklistSubscriptionRequest,
  UpdateStocklistSubscriptionRequest,
} from './email-subscriptions.types';

export const useEmailSubscriptions = (
  contactEmployeeId: number | null,
  enabled = true,
) =>
  useQuery<EmailSubscription[]>({
    queryKey: queryKeys.emailSubscriptions.byContact(contactEmployeeId!),
    queryFn: () => emailSubscriptionsApi.getByContact(contactEmployeeId!),
    enabled: contactEmployeeId != null && enabled,
  });

export const useUpsertStocklistSubscription = () => {
  const qc = useQueryClient();
  return useMutation<
    EmailSubscription,
    AxiosError,
    { contactEmployeeId: number; body: UpdateStocklistSubscriptionRequest }
  >({
    mutationFn: ({ contactEmployeeId, body }) =>
      emailSubscriptionsApi.upsertStocklist(contactEmployeeId, body),
    onSuccess: (_data, { contactEmployeeId }) =>
      qc.invalidateQueries({
        queryKey: queryKeys.emailSubscriptions.byContact(contactEmployeeId),
      }),
  });
};

export const useDeleteStocklistSubscription = () => {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, number>({
    mutationFn: (contactEmployeeId) =>
      emailSubscriptionsApi.deleteStocklist(contactEmployeeId),
    onSuccess: (_data, contactEmployeeId) =>
      qc.invalidateQueries({
        queryKey: queryKeys.emailSubscriptions.byContact(contactEmployeeId),
      }),
  });
};

export const useTestSendStocklist = () =>
  useMutation<{ message: string }, AxiosError, number>({
    mutationFn: (contactEmployeeId) =>
      emailSubscriptionsApi.testSendStocklist(contactEmployeeId),
  });

export const useSendStocklist = () =>
  useMutation<
    { message: string },
    AxiosError,
    { contactEmployeeId: number; email?: string }
  >({
    mutationFn: ({ contactEmployeeId, email }) =>
      emailSubscriptionsApi.sendStocklist(contactEmployeeId, email),
  });

// --- Customer self-service (JWT-scoped) ---

export const useMyEmailSubscriptions = () =>
  useQuery<EmailSubscription[]>({
    queryKey: queryKeys.emailSubscriptions.mine,
    queryFn: () => emailSubscriptionsApi.getMine(),
  });

export const useUpsertMyStocklistSubscription = () => {
  const qc = useQueryClient();
  return useMutation<
    EmailSubscription,
    AxiosError,
    MeUpdateStocklistSubscriptionRequest
  >({
    mutationFn: (body) => emailSubscriptionsApi.upsertMyStocklist(body),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.emailSubscriptions.mine,
      }),
  });
};

export const useDeleteMyStocklistSubscription = () => {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, number | undefined>({
    mutationFn: (contactEmployeeId) =>
      emailSubscriptionsApi.deleteMyStocklist(contactEmployeeId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.emailSubscriptions.mine,
      }),
  });
};

export const useUnsubscribeFromEmails = () =>
  useMutation<{ message: string }, AxiosError, string>({
    mutationFn: (token) => emailSubscriptionsApi.unsubscribe(token),
  });
